import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import Section from "../sections/Section";
import useSections from "../sections/useSections.js";
import BuilderSkeletonPlaceholder from "../variants/BuilderSkeletonPlaceholder";
import { buildStepStylesheet } from "./StepStyleResolver";

const Step = (props) => {
  let cta = props.cta;
  let variant = props.variant;
  let step = props.step;

  if (step.placeholder == true) {
    return (
      <div
        id={"step_" + step.id}
        className="cf-step pointer-events-none"
        data-variant-id={variant.id}
        data-step-id={step.id}
        data-step-position={step.position}
        data-object-type="steps"
        data-object-id={step.id}
      >
        <div className="cf-step-inner">
          <BuilderSkeletonPlaceholder object_type="steps" />
        </div>
      </div>
    );
  }
  const sorted_sections = Object.values({ ...variant.sections, ...step.sections })
    .filter(
      (section) =>
        section.step_id == step.id &&
        !section.element_id &&
        section.toBeDeleted !== true
    )
    .sort((a, b) => a.position - b.position);
  const childIndexes = buildChildIndexes(variant, step);

  const classes = style.classes(cta, variant, step);
  const stylesheet = buildStepStylesheet(
    props.builder,
    props.device,
    cta,
    variant,
    step
  );

  let important = props.builder !== true ? " !important" : "";

  let styling = {};

  if (step.position == 1) {
    styling["display"] = "block" + important;
    styling["visibility"] = "initial" + important;
  }

  function variantWithoutChildren(variant) {
    let response = {};
    Object.keys(variant)
      .filter(
        (k) =>
          [
            "steps",
            "sections",
            "elements",
            "fields",
            "content_list_items",
            "field_options",
            "actions",
            "action_groups",
            "content_toggle_items",
            "conditions",
            "products",
            "product_variants",
            "product_recommendations",
          ].includes(k) == false
      )
      .forEach((key) => (response[key] = variant[key]));
    return response;
  }

  function sectionWithChildren(section) {
    if (section) {
      let response = {
        ...section,
        element: childIndexes.elementsById[section.element_id],
      };
      response.elements = {};
      response.section_columns = {};

      (childIndexes.sectionColumnsBySectionId[section.id] || []).forEach(
        (sc) => {
          response.section_columns[sc.id] = { ...sc, elements: {} };
        }
      );

      (childIndexes.elementsBySectionId[section.id] || []).forEach(
        (element) => {
          let elementReponse = {
            ...element,
            sections: {},
            fields: {},
            content_list_items: {},
            products: {},
            product_recommendations: {},
            content_toggle_items: {},
          };

          (childIndexes.sectionsByElementId[element.id] || []).forEach(
            (row) =>
              (elementReponse.sections[row.id] = sectionWithChildren(row))
          );

          (childIndexes.fieldsByElementId[element.id] || []).forEach(
            (field) => {
              let fieldResponse = { ...field, field_options: {} };

              (childIndexes.fieldOptionsByFieldId[field.id] || []).forEach(
                (field_option) =>
                  (fieldResponse.field_options[field_option.id] = {
                    ...field_option,
                  })
              );
              elementReponse.fields[field.id] = { ...fieldResponse };
            }
          );

          (
            childIndexes.contentToggleItemsByElementId[element.id] || []
          ).forEach((list_item) => {
            elementReponse.content_toggle_items[list_item.id] = {
              ...list_item,
            };
          });

          (childIndexes.contentListItemsByElementId[element.id] || []).forEach(
            (list_item) => {
              elementReponse.content_list_items[list_item.id] = {
                ...list_item,
              };
            }
          );

          (childIndexes.productsByElementId[element.id] || []).forEach(
            (product) => {
              let productResponse = { ...product, product_variants: {} };

              (
                childIndexes.productVariantsByProductId[product.id] || []
              ).forEach(
                (product_variant) =>
                  (productResponse.product_variants[product_variant.id] = {
                    ...product_variant,
                  })
              );
              elementReponse.products[product.id] = { ...productResponse };
            }
          );

          (
            childIndexes.productRecommendationsByElementId[element.id] || []
          ).forEach((product_recommendation) => {
            let productRecommendationResponse = {
              ...product_recommendation,
              products: {},
            };

            (
              childIndexes.productsByProductRecommendationId[
                product_recommendation.id
              ] || []
            ).forEach((product) => {
              let productResponse = { ...product, product_variants: {} };

              (
                childIndexes.productVariantsByProductId[product.id] || []
              ).forEach(
                (product_variant) =>
                  (productResponse.product_variants[product_variant.id] = {
                    ...product_variant,
                  })
              );

              productRecommendationResponse.products[product.id] = {
                ...productResponse,
              };
              elementReponse.products[product.id] = { ...productResponse };
            });

            elementReponse.product_recommendations[product_recommendation.id] =
              {
                ...productRecommendationResponse,
              };
          });

          if (
            elementReponse.section_column_id &&
            response.section_columns[elementReponse.section_column_id]
          ) {
            response.section_columns[elementReponse.section_column_id].elements[
              elementReponse.id
            ] = elementReponse;
          }

          response.elements[element.id] = elementReponse;
        }
      );

      return response;
    }
  }

  function getSectionProps(section) {
    return {
      builder: props.builder,
      device: props.device,
      user: props.user,
      website: props.website,
      cta: props.cta,
      variant: variantWithoutChildren(variant),
      step: step,
      section: sectionWithChildren(section),
      preview: props.preview,
    };
  }

  let Sections;
  if (props.builder == true) {
    const [contextData, builder] = useContext(VariantContextData);
    const { currentHook } = useSections(step);
    Sections = currentHook;

    useEffect(() => {
      Sections.sortableStart();
    });
  }

  return (
    <React.Fragment>
      <div
        id={"step_" + step.id}
        className={classes}
        data-variant-id={variant.id}
        data-progress-bar-count={
          step?.options?.["progress-bar-count"] != "false" ? true : false
        }
        step-id={step.id}
        data-step-id={step.id}
        data-step-position={step.position}
        data-object-type="steps"
        data-object-id={step.id}
        style={styling}
      >
        <div className="cf-step-inner">
          <div
            className="cf-cta-container cf-row cf-outer-center"
            data-variant-id={variant.id}
            data-object-id={step.id}
          >
            <div
              className="cf-cta-container-overlay cf-background-overlay"
              data-variant-id={variant.id}
            ></div>
            {sorted_sections.map((section, i) => {
              return (
                <Section
                  key={"section-" + section.id}
                  {...getSectionProps(section)}
                />
              );
            })}
          </div>
          {props.builder == true && (
            <div
              className="section-add-container text-blue-600 dark:text-white"
              onClick={() => {
                dispatchCustomEvent("newObject", {
                  object_type: "Sections",
                  step_id: step.id,
                });
              }}
              style={{
                display: "block",
              }}
            >
              <span
                className="glyphicon glyphicon-plus-sign"
                aria-hidden="true"
              ></span>{" "}
              Add Section
            </div>
          )}
        </div>
        <style dangerouslySetInnerHTML={{ __html: stylesheet }} />
      </div>
    </React.Fragment>
  );
};

const style = {
  classes: (cta, variant, step) => {
    let classes = "cf-step cf-cta-container-background";

    if (step.loaded == false) {
      classes += " opacity-50 pointer-events-none";
    }

    if (step.options && step.options["custom-class"]) {
      classes += " " + step.options["custom-class"];
    }

    classes += variant.options["background-position"]
      ? " cf-background-position-" + variant.options["background-position"]
      : " cf-background-position-top-center";

    classes += variant.options["background-size"]
      ? " cf-background-size-" + variant.options["background-size"]
      : " cf-background-size-cover";

    return classes;
  },
};

const addToIndex = (index, key, value) => {
  if (!key) return;
  if (!index[key]) index[key] = [];

  index[key].push(value);
};

const buildChildIndexes = (variant, step) => {
  let indexes = {
    elementsById: {},
    elementsBySectionId: {},
    sectionsByElementId: {},
    fieldsByElementId: {},
    fieldOptionsByFieldId: {},
    contentToggleItemsByElementId: {},
    contentListItemsByElementId: {},
    productsByElementId: {},
    productsByProductRecommendationId: {},
    productVariantsByProductId: {},
    productRecommendationsByElementId: {},
    sectionColumnsBySectionId: {},
  };

  Object.values(variant.section_columns || {}).forEach((sectionColumn) => {
    if (!sectionColumn) return;

    addToIndex(
      indexes.sectionColumnsBySectionId,
      sectionColumn.section_id,
      sectionColumn
    );
  });

  Object.values(variant.elements || {}).forEach((element) => {
    if (!element) return;

    indexes.elementsById[element.id] = element;
    addToIndex(indexes.elementsBySectionId, element.section_id, element);
  });

  Object.values({
    ...(variant.sections || {}),
    ...(step.sections || {}),
  }).forEach((section) => {
    if (!section || !section.element_id) return;

    addToIndex(indexes.sectionsByElementId, section.element_id, section);
  });

  Object.values(variant.fields || {}).forEach((field) => {
    addToIndex(indexes.fieldsByElementId, field.element_id, field);
  });

  Object.values(variant.field_options || {}).forEach((fieldOption) => {
    addToIndex(
      indexes.fieldOptionsByFieldId,
      fieldOption.field_id,
      fieldOption
    );
  });

  Object.values(variant.content_toggle_items || {}).forEach((item) => {
    addToIndex(indexes.contentToggleItemsByElementId, item.element_id, item);
  });

  Object.values(variant.content_list_items || {}).forEach((item) => {
    addToIndex(indexes.contentListItemsByElementId, item.element_id, item);
  });

  Object.values(variant.products || {}).forEach((product) => {
    addToIndex(indexes.productsByElementId, product.element_id, product);
    addToIndex(
      indexes.productsByProductRecommendationId,
      product.product_recommendation_id,
      product
    );
  });

  Object.values(variant.product_variants || {}).forEach((productVariant) => {
    addToIndex(
      indexes.productVariantsByProductId,
      productVariant.product_id,
      productVariant
    );
  });

  Object.values(variant.product_recommendations || {}).forEach(
    (recommendation) => {
      addToIndex(
        indexes.productRecommendationsByElementId,
        recommendation.element_id,
        recommendation
      );
    }
  );

  return indexes;
};

export default Step;
